import React, { useMemo } from "react";
import { Box, BoxProps, Typography } from "@mui/material";
import { centerContent, columnFlex, flex1 } from "src/styles/sx";
import { mergeSx } from "src/utils/styles";
import i18n from "i18next";
import axios from "axios";
import { stringToHtml } from "src/utils/strings";
import { AavoBackendError } from "src/errorHandling/aavoBackendError";
import { AnyError, isAavoBackendError, isAavoErrorResponse } from "src/errorHandling/anyError";
import { isError } from "src/errorHandling/errorUtils";

export interface ErrorTextProps extends BoxProps {
	error: AnyError;
}

export const ErrorText = ({ error, sx, ...other }: ErrorTextProps) => {
	const errorContent = useMemo(() => {
		const content: React.ReactNode[] = [];
		const [backendError, message, status] = parseError(error);
		if (backendError) {
			content.push(stringToHtml(backendError.message));
			if (backendError.logId !== undefined)
				content.push(
					<Typography key={"aavo-error-text-log-id"}>{`${i18n.t("log_id")}: ${
						backendError.logId
					}`}</Typography>,
				);
		} else if (message) {
			content.push(<Typography key={"aavo-error-text-message"}>{message}</Typography>);
		}
		if (status) {
			content.push(
				<Typography key={"aavo-error-text-status-code"}>{`${i18n.t(
					"status_code",
				)}: ${status}`}</Typography>,
			);
		}

		return content;
	}, [error]);
	return (
		<Box sx={mergeSx(columnFlex, flex1, centerContent, sx ?? {})} {...other}>
			{errorContent}
		</Box>
	);
};

const parseError = (
	error: AnyError,
): [
	errorObject: AavoBackendError | undefined,
	message: string | undefined,
	status: number | undefined,
] => {
	let errorObject: AavoBackendError | undefined = undefined;
	let status: number | undefined = undefined;
	let message: string | undefined = undefined;
	if (isAavoErrorResponse(error)) {
		return parseError(error.response.data);
	} else if (isAavoBackendError(error)) {
		errorObject = error;
	} else if (axios.isAxiosError(error)) {
		status = error.response?.status;
		message = error.response?.data ?? error.message;
	} else if (isError(error)) {
		message = error.message;
	} else {
		message = error;
	}
	return [errorObject, message, status];
};
